import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {useKeycloak} from '@react-keycloak/web';

import {Button, Col, Divider, Form, message, Row} from 'antd';
import {AiFillEye} from 'react-icons/all';
import AppTableContainer from '@crema/core/AppTableContainer';
import DiagnosticInfo from './DiagnosticInfo';
import PractitionerSelect from 'pages/components/PractitionerSelect';
import DiseaseSelect from 'pages/components/DiseaseSelect';
import SpecialitySelect from 'pages/components/SpecialitySelect';

import {findRequests} from 'services/RequestService';
import {handleError} from 'utils/MessageUtils';
import {EQUAL, JSON_EXTRACT_EQUAL} from 'shared/constants/QueryModes';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';

const DiagnosticList = (props) => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const [form] = Form.useForm();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const {patientId} = props;
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [searchValues, setSearchValues] = useState();

  const loadData = (params) => {
    const filters = [];
    if (params.filters) {
      Object.keys(params.filters).map((key) =>
        filters.push({
          fields: key,
          value: params.filters[key],
        }),
      );
    }

    filters.push({
      fields: 'patient.id',
      mode: EQUAL,
      value: patientId,
    });

    if (searchValues?.speciality?.id) {
      filters.push({
        fields: 'speciality',
        mode: JSON_EXTRACT_EQUAL,
        value: ['id', searchValues.speciality.id],
      });
    }

    if (searchValues?.disease?.id) {
      filters.push({
        fields: 'diagnostic',
        mode: JSON_EXTRACT_EQUAL,
        value: ['disease', 'id', searchValues.disease.id],
      });
    }

    if (searchValues?.performer?.id) {
      filters.push({
        fields: 'performer.id',
        mode: EQUAL,
        value: searchValues.performer.id,
      });
    }

    setLoading(true);
    findRequests(headers, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        setRequests(res.data);
        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData({pagination});
  }, [patientId]);

  useEffect(() => {
    if (searchValues) {
      loadData({
        pagination: {...pagination, current: 1, pageSize: 20},
      });
    }

    form.resetFields();
  }, [searchValues]);

  const onView = (request) => {
    setSelectedRequest(request);
    setViewing(true);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const onSearch = (values) => {
    setSearchValues(values);
  };

  const onClearSearch = () => {
    setSearchValues({});
  };

  const columns = [
    {
      title: intl.messages['diagnostic.disease'],
      dataIndex: 'diagnostic',
      key: 'diagnostic',
      render: (diagnostic) => <span>{diagnostic?.disease?.name}</span>,
    },
    {
      title: intl.messages['request.performer'],
      dataIndex: 'performer',
      key: 'performer',
      render: (performer) => <span>{performer?.name}</span>,
    },
    {
      title: intl.messages['request.speciality'],
      dataIndex: 'speciality',
      key: 'speciality',
      render: (speciality) => <span>{speciality?.name}</span>,
    },
    {
      title: intl.messages['request.symptom'],
      dataIndex: ['symptom'],
      key: 'symptom',
      render: (symptom) => <span>{symptom}</span>,
    },
    {
      title: intl.messages['diagnostic.disease'],
      dataIndex: 'diagnostic',
      key: 'diagnostic',
      render: (diagnostic) => <span>{diagnostic?.conclusion}</span>,
    },
    {
      title: intl.messages['common.actions'],
      dataIndex: 'id',
      key: 'actions',
      className: 'customer-table-actions',
      fixed: 'right',
      render: (id, record) => (
        <Button
          type='primary'
          shape='circle'
          ghost
          icon={<AiFillEye />}
          onClick={() => onView(record)}
        />
      ),
    },
  ];

  return (
    <>
      <Form
        layout='vertical'
        form={form}
        initialValues={searchValues}
        onFinish={onSearch}>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name={['speciality', 'id']}
              label={intl.messages['request.speciality']}>
              <SpecialitySelect
                onChange={(value) => {
                  form.setFieldsValue({speciality: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({speciality: null});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['disease', 'id']}
              label={intl.messages['diagnostic.disease']}>
              <DiseaseSelect
                onChange={(value) => {
                  form.setFieldsValue({disease: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({disease: null});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name={['performer', 'id']}
              label={intl.messages['request.performer']}>
              <PractitionerSelect
                onChange={(value) => {
                  form.setFieldsValue({performer: {id: value}});
                }}
                onClear={() => {
                  form.setFieldsValue({performer: null});
                }}
              />
            </Form.Item>
          </Col>
          <Col span={6}></Col>
        </Row>
        <Row gutter={16}>
          <Col span={10} />
          <Col span={2}>
            <Button onClick={onClearSearch} style={{width: '100%'}}>
              {intl.messages['common.reset']}
            </Button>
          </Col>
          <Col span={2}>
            <Button type='primary' htmlType='submit' style={{width: '100%'}}>
              {intl.messages['common.search']}
            </Button>
          </Col>
          <Col span={10} />
        </Row>
      </Form>

      <Divider />

      <AppTableContainer
        className='customer-table'
        hoverColor
        data={requests}
        columns={columns}
        rowKey={(record) => record.id}
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />

      <DiagnosticInfo
        request={selectedRequest}
        viewing={viewing}
        setViewing={setViewing}
      />
    </>
  );
};

export default DiagnosticList;

DiagnosticList.propTypes = {
  patientId: PropTypes.string,
};
